import type { AgentResults, TournamentResults } from '@/api/types';
import { formatCount, formatMoney, formatPercent } from '@/lib/format';
import { Table, Td, Th } from '@/ui/Table';
import { WINDOWS, windowHeading } from './windows';

function Row({ agent, total }: { agent: AgentResults; total?: boolean }) {
  const emphasis = total ? 'bg-slate-50 font-semibold' : '';
  return (
    <tr className={emphasis}>
      <Td align="left">{agent.agentId === null ? 'All agents' : agent.agentId}</Td>
      <Td>{formatCount(agent.linksIssued)}</Td>
      <Td>{formatCount(agent.playersInvited)}</Td>
      <Td>{formatCount(agent.playersRedeemed)}</Td>
      <Td>{formatPercent(agent.redeemRate)}</Td>
      {WINDOWS.map((window) => {
        const metrics = agent.retention[window.key];
        return [
          <Td key={`${window.key}-active`}>
            {formatCount(metrics.activePlayers)} ({formatPercent(metrics.retentionRate)})
          </Td>,
          <Td key={`${window.key}-dep`}>{formatCount(metrics.depositors)}</Td>,
          <Td key={`${window.key}-eur`}>{formatMoney(metrics.depositEur)}</Td>,
          <Td key={`${window.key}-ggr`}>{formatMoney(metrics.ggrEur)}</Td>,
        ];
      })}
    </tr>
  );
}

export function ResultsTable({ results }: { results: TournamentResults }) {
  return (
    <Table>
      <thead>
        <tr>
          <Th colSpan={5} />
          {WINDOWS.map((window) => (
            <Th
              key={window.key}
              colSpan={4}
              align="center"
              className={results.windows[window.key].partial ? 'text-amber-700' : ''}
            >
              {windowHeading(results.windows, window.key, window.label)}
            </Th>
          ))}
        </tr>
        <tr>
          <Th align="left">Agent</Th>
          <Th>Links</Th>
          <Th>Invited</Th>
          <Th>Joined</Th>
          <Th>Join rate</Th>
          {WINDOWS.map((window) => [
            <Th key={`${window.key}-active`}>Active (rate)</Th>,
            <Th key={`${window.key}-dep`}>Depositors</Th>,
            <Th key={`${window.key}-eur`}>Deposits €</Th>,
            <Th key={`${window.key}-ggr`}>GGR €</Th>,
          ])}
        </tr>
      </thead>
      <tbody>
        {results.agents.map((agent) => (
          <Row key={agent.agentId ?? 'all'} agent={agent} />
        ))}
        {/* An agent's report holds only their own row; the totals line would repeat it. */}
        {results.agents.length !== 1 && <Row agent={results.totals} total />}
      </tbody>
    </Table>
  );
}
